import {
  Children,
  forwardRef,
  type ElementType,
  type ReactElement,
} from 'react';

import { getAppName } from '@dynatrace-sdk/app-environment';

import { Button } from '../../buttons/button/Button.js';
import {
  ReleasePhaseIndicator,
  type ReleasePhase,
} from '../../content/release-phase-indicator/ReleasePhaseIndicator.js';
import type { AriaLabelingProps } from '../../core/types/a11y-props.js';
import type { DataTestId } from '../../core/types/data-props.js';
import type { MaskingProps } from '../../core/types/masking-props.js';
import type { PolymorphicComponentProps } from '../../core/types/polymorph.js';
import type { StylingProps } from '../../core/types/styling-props.js';
import type { WithChildren } from '../../core/types/with-children.js';
import { isSlot } from '../../core/utils/is-slot.js';
import { AppIcon, type AppIconProps } from './app-icon/AppIcon.js';

/**
 * @public
 */
export interface AppHeaderLogoOwnProps
  extends
    DataTestId,
    MaskingProps,
    StylingProps,
    AriaLabelingProps,
    WithChildren {
  appName?: string;
  appIcon?: string;
  href?: string;
  /**
   * Indicates the release phase of the app. When set, a chip is shown to the
   * right of the app name indicating the phase (e.g. `'preview'`, `'early-access'`).
   */
  releasePhase?: ReleasePhase;
}

// DEPRECATION: [APPDEV-14264] BREAKING-CHANGE: AppHeader: Remove old NavItems API.
/**
 * @public
 * @deprecated `Logo` replaces `AppNavLink`
 */
export interface AppNavLinkInternalOwnProps extends AppHeaderLogoOwnProps {
  /** Has no effect. Will be deprecated. */
  pin?: boolean;
}

/**
 * @public
 */
export type AppHeaderLogoProps<E extends ElementType> =
  PolymorphicComponentProps<E, AppHeaderLogoOwnProps>;

/**
 * App logo with icon and app name.
 * @public
 */
export const Logo: <E extends ElementType = 'a'>(
  props: AppHeaderLogoProps<E>,
) => ReactElement | null = /* @__PURE__ */ forwardRef(
  <E extends ElementType>(
    props: AppHeaderLogoProps<E>,
    ref: typeof props.ref,
  ) => {
    const {
      appIcon,
      appName,
      as: propAs,
      href = '/',
      className,
      style,
      releasePhase,
      'data-testid': dataTestId,
      ...rest
    }: AppHeaderLogoProps<E> = props;
    const as = propAs ?? 'a';
    const name = appName ?? getAppName();

    let icon: ReactElement<AppIconProps> | undefined;
    for (const child of Children.toArray(props.children)) {
      if (isSlot(child, AppIcon)) {
        icon = child;
      }
    }

    const hasReleasePhaseIndicator = releasePhase !== undefined;

    return (
      <Button<ElementType>
        {...rest}
        data-testid={dataTestId}
        as={as}
        ref={ref}
        href={href}
        className={className}
        style={style}
      >
        <Button.Prefix>
          <AppIcon src={appIcon ?? icon?.props.src} alt="" />
        </Button.Prefix>
        <Button.Label
          style={hasReleasePhaseIndicator ? { paddingRight: 0 } : undefined}
        >
          {name}
        </Button.Label>
        {hasReleasePhaseIndicator ? (
          <Button.Suffix>
            <ReleasePhaseIndicator phase={releasePhase} />
          </Button.Suffix>
        ) : null}
      </Button>
    );
  },
);

(Logo as typeof Logo & { displayName: string }).displayName = 'AppHeader.Logo';
